import { observer } from 'mobx-react';
import React from 'react';
import { DefaultPageViewModel } from '../default-page.viewmodel';
import { MY_SCHEDULE_NOTES, RESTRICT_ACCESS_ENABLED, RESTRICT_ACCESS_FILTERS, TOPIC_ENABLED } from '../../../../../../../../modules/passport-pages/config';
import { FormField } from '../../../../../../../global/form/form-field/form-field';
import { OEToggle } from '../../../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../../../global/text/text';
import { FormattedMessage, useIntl } from 'react-intl';
import { InfoPopover } from '../../../../../../../global/info-popover/info-popover';
import { RichTextEditor } from '../../../../../../../global/rich-text-editor';
import { RestrictAccessContainer } from '../restrict-access-container';
import { CustomFieldsOptionsListDTO } from '../../../../../../../../api/custom-pages.dao';

type Props = {
    pageVm: DefaultPageViewModel;
    organizationTypes: string[];
    customFieldsOptionsList: CustomFieldsOptionsListDTO[];
};

export const AgendaPageFields = observer(({
    pageVm,
    organizationTypes,
    customFieldsOptionsList
}: Props): JSX.Element => {
    const { formatMessage } = useIntl();
    const { value: topicEnabled } = pageVm.getFieldByKey(TOPIC_ENABLED);
    const toggleTopicEnabled = () => pageVm.setFieldValueByKey(TOPIC_ENABLED, !topicEnabled);
    const myScheduleNotesEntity = pageVm.getFieldConfigByKey(MY_SCHEDULE_NOTES);
    const myScheduleNotesLabelText = formatMessage(myScheduleNotesEntity.labelDescriptor);
    const myScheduleNotes = pageVm.getFieldByKey(MY_SCHEDULE_NOTES);

    const {
        value: restrictAccess,
        touched: restrictAccessTouched,
    } = pageVm.getFieldByKey(RESTRICT_ACCESS_ENABLED);

    const toggleRestrictAccess = () => {
        pageVm.setFieldValueByKey(RESTRICT_ACCESS_ENABLED, !restrictAccess);
        if (restrictAccess) {
            pageVm.setFieldValueByKey(RESTRICT_ACCESS_FILTERS, []);
        }
        if (!restrictAccessTouched) {
            pageVm.setValueTouched(RESTRICT_ACCESS_ENABLED);
        }
    };

    return (
        <>
            <FormField label={
                <div className='flex m-bottom-6px'>
                    {myScheduleNotesLabelText}
                    <InfoPopover className='m-left-8px'>
                        {myScheduleNotesEntity.toggleInfoMessage}
                    </InfoPopover>
                </div>
            }>
                <div className='flex'>
                    <RichTextEditor
                        className='oe-form-field__control p-0px m-0px'
                        onBlur={() => pageVm.setValueTouched.bind(pageVm, MY_SCHEDULE_NOTES)()}
                        value={myScheduleNotes.value as string || ''}
                        onChange={(e, editor) => pageVm.setFieldValueByKey(MY_SCHEDULE_NOTES, editor.getData())}
                    />
                </div>
                <span className='oe-form-field__error'>
                    {
                        !myScheduleNotes.valid && myScheduleNotes.touched &&
                        <FormattedMessage values={{ fieldName: myScheduleNotesLabelText }} {...myScheduleNotes.validationMessage} />
                    }
                </span>
            </FormField>
            <FormField>
                <div className='flex'>
                    <OEToggle
                        isSelected={topicEnabled as boolean}
                        onClick={toggleTopicEnabled}
                    />
                    <Text
                        message='Display Meeting Topic for all "Closed" meetings on the My Schedule'
                        id='oe.customPage.agendaTopicEnabled'
                        description='Label text'
                    />
                </div>
            </FormField>
            <FormField>
                <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                    <OEToggle
                        isSelected={ restrictAccess as boolean}
                        onClick={toggleRestrictAccess}
                    />
                    {restrictAccess ?
                        <Text
                            message='Access to My Schedule page is restricted'
                            id='oe.defaultPage.accessToMyScheduleRestricted'
                            description='Label text'
                        /> :
                        <Text
                            message='Access to My Schedule page is not restricted'
                            id='oe.defaultPage.accessToMyScheduleNotRestricted'
                            description='Label text'
                        />
                    }
                </div>
                <div className={'font-size-12px line-height-21px m-top-15px'}>
                    <Text message='When access to My Schedule page is restricted, attendees will only have access
                    to the My Schedule page if they match all the criteria of at least one filter.'
                          id='oe.defaultPage.accessToMyScheduleDescription'
                          description='Label text'
                    />
                </div>
            </FormField>
            {restrictAccess
                ? <RestrictAccessContainer
                    pageVm={pageVm}
                    organizationTypes={organizationTypes}
                    customFieldsOptionsList={customFieldsOptionsList}
                    maxFiltersCount={20}
                />
                : null
            }
        </>
    );
});
